<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-min-h-0 tw-overflow-hidden ">
    <!-- Chat Header -->
    <div class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50">
      <div class="tw-flex tw-items-center tw-gap-3">
        <!-- User Avatar -->
        <div 
          class="tw-w-10 tw-h-10 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden tw-cursor-pointer tw-transition-transform hover:tw-scale-105"
          @click="navigateToUserProfile(otherUser?.id)"
          :title="otherUser?.name ? `View ${otherUser.name}'s profile` : 'View profile'"
        >
          <img 
            v-if="otherUser?.profile_image" 
            :src="otherUser.profile_image" 
            :alt="otherUser.name"
            class="tw-w-full tw-h-full tw-object-cover"
          />
          <div v-else class="tw-w-full tw-h-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
            <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-lg" />
          </div>
        </div>
        
        <!-- User Info -->
        <div class="tw-flex-1">
          <h3 
            v-if="!participantsLoading" 
            class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-cursor-pointer hover:tw-text-primary-600 tw-transition-colors"
            @click="navigateToUserProfile(otherUser?.id)"
            :title="otherUser?.name ? `View ${otherUser.name}'s profile` : 'View profile'"
          >
            {{ otherUser?.name || 'Unknown User' }}
          </h3>
          <div v-else class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
          
          <p v-if="!loading && !participantsLoading" class="tw-text-sm tw-text-gray-600">
            {{ messages.length }} message{{ messages.length !== 1 ? 's' : '' }}
          </p>
          <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
        </div>
      </div>
      
      <!-- Header Actions -->
      <div class="tw-flex tw-items-center tw-gap-2">
        <!-- Call Status Indicator -->
        <CallStatusIndicator
          v-if="videoCallState.active"
          :call-active="videoCallState.active"
          :is-minimized="videoCallState.minimized"
          :call-type="videoCallState.type"
          :other-participant="otherUser"
          :call-start-time="videoCallState.startTime"
          :room-id="videoCallState.roomId"
          :conversation-id="conversationId"
          :call-id="videoCallState.callId"
          @restore-call="handleRestoreCall"
          @end-call="handleEndCall"
        />

        <!-- Video Call Button -->
        <VideoCallButton
          v-if="!videoCallState.active"
          :conversation-id="conversationId"
          :other-participant="otherUser"
          :disabled="participantsLoading || !otherUser"
          @call-initiated="handleCallInitiated"
          @error="handleCallError"
        />

        <button
          @click="refreshMessages"
          :disabled="loading"
          class="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
          title="Refresh messages"
        >
          <icon icon="solar:refresh-bold-duotone" class="tw-text-lg" />
        </button>
      </div>
    </div>

    <!-- Enhanced Loading State -->
    <div v-if="loading" class="tw-flex-1 tw-p-4 tw-space-y-6">


      <!-- Message Skeletons -->
      <div class="tw-space-y-6">
        <!-- Received Message Skeleton 1 -->
        <div class="tw-flex tw-items-start tw-gap-3">
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
          
          <!-- Message Content -->
          <div class="tw-flex tw-flex-col tw-max-w-[70%] tw-items-start">
            <!-- Sender Name Skeleton -->
            <div class="tw-mb-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse" style="animation-delay: 0.1s;"></div>
            </div>
            
            <!-- Message Bubble Skeleton -->
            <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tl-none tw-px-3 tw-py-2 tw-max-w-full">
              <div class="tw-space-y-2">
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-48 tw-animate-pulse" style="animation-delay: 0.2s;"></div>
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse" style="animation-delay: 0.3s;"></div>
              </div>
            </div>
            
            <!-- Timestamp Skeleton -->
            <div class="tw-mt-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse" style="animation-delay: 0.4s;"></div>
            </div>
          </div>
        </div>

        <!-- Sent Message Skeleton 1 -->
        <div class="tw-flex tw-items-start tw-gap-3 tw-justify-end">
          <!-- Message Content -->
          <div class="tw-flex tw-flex-col tw-max-w-[70%] tw-items-end">
            <!-- Message Bubble Skeleton -->
            <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tr-none tw-px-3 tw-py-2 tw-max-w-full">
              <div class="tw-space-y-2">
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-animate-pulse" style="animation-delay: 0.5s;"></div>
              </div>
            </div>
            
            <!-- Timestamp Skeleton -->
            <div class="tw-mt-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse" style="animation-delay: 0.6s;"></div>
            </div>
          </div>
          
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
        </div>

        <!-- Received Message Skeleton 2 (File Message) -->
        <div class="tw-flex tw-items-start tw-gap-3">
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
          
          <!-- Message Content -->
          <div class="tw-flex tw-flex-col tw-max-w-[70%] tw-items-start">
            <!-- Sender Name Skeleton -->
            <div class="tw-mb-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse" style="animation-delay: 0.7s;"></div>
            </div>
            
            <!-- File Message Bubble Skeleton -->
            <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tl-none tw-px-3 tw-py-2 tw-max-w-full">
              <div class="tw-flex tw-items-center tw-gap-2">
                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse" style="animation-delay: 0.8s;"></div>
                <div class="tw-flex-1 tw-min-w-0">
                  <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse" style="animation-delay: 0.9s;"></div>
                  <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse" style="animation-delay: 1.0s;"></div>
                </div>
                <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse" style="animation-delay: 1.1s;"></div>
              </div>
            </div>
            
            <!-- Timestamp Skeleton -->
            <div class="tw-mt-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse" style="animation-delay: 1.2s;"></div>
            </div>
          </div>
        </div>

        <!-- Sent Message Skeleton 2 -->
        <div class="tw-flex tw-items-start tw-gap-3 tw-justify-end">
          <!-- Message Content -->
          <div class="tw-flex tw-flex-col tw-max-w-[70%] tw-items-end">
            <!-- Message Bubble Skeleton -->
            <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tr-none tw-px-3 tw-py-2 tw-max-w-full">
              <div class="tw-space-y-2">
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-40 tw-animate-pulse" style="animation-delay: 1.3s;"></div>
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-animate-pulse" style="animation-delay: 1.4s;"></div>
              </div>
            </div>
            
            <!-- Timestamp Skeleton -->
            <div class="tw-mt-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse" style="animation-delay: 1.5s;"></div>
            </div>
          </div>
          
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
        </div>

        <!-- System Message Skeleton -->
        <div class="tw-flex tw-items-center tw-justify-center tw-py-2">
          <div class="tw-bg-gray-50 tw-rounded-full tw-px-4 tw-py-1">
            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse" style="animation-delay: 1.6s;"></div>
          </div>
        </div>

        <!-- Received Message Skeleton 3 -->
        <div class="tw-flex tw-items-start tw-gap-3">
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
          
          <!-- Message Content -->
          <div class="tw-flex tw-flex-col tw-max-w-[70%] tw-items-start">
            <!-- Sender Name Skeleton -->
            <div class="tw-mb-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse" style="animation-delay: 1.7s;"></div>
            </div>
            
            <!-- Message Bubble Skeleton -->
            <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tl-none tw-px-3 tw-py-2 tw-max-w-full">
              <div class="tw-space-y-2">
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-56 tw-animate-pulse" style="animation-delay: 1.8s;"></div>
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-44 tw-animate-pulse" style="animation-delay: 1.9s;"></div>
                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-animate-pulse" style="animation-delay: 2.0s;"></div>
              </div>
            </div>
            
            <!-- Timestamp Skeleton -->
            <div class="tw-mt-1">
              <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse" style="animation-delay: 2.1s;"></div>
            </div>
          </div>
        </div>

        <!-- Typing Indicator Skeleton -->
        <div class="tw-flex tw-items-start tw-gap-3">
          <!-- Avatar Skeleton -->
          <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
          
          <!-- Typing Bubble Skeleton -->
          <div class="tw-bg-gray-100 tw-rounded-lg tw-rounded-tl-none tw-px-3 tw-py-2">
            <div class="tw-flex tw-items-center tw-gap-1">
              <div class="tw-w-2 tw-h-2 tw-bg-gray-300 tw-rounded-full tw-animate-pulse" style="animation-delay: 0.1s;"></div>
              <div class="tw-w-2 tw-h-2 tw-bg-gray-300 tw-rounded-full tw-animate-pulse" style="animation-delay: 0.2s;"></div>
              <div class="tw-w-2 tw-h-2 tw-bg-gray-300 tw-rounded-full tw-animate-pulse" style="animation-delay: 0.3s;"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && messages.length === 0" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-min-h-[400px]">
      <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
        <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
      </div>
      <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No messages yet</h3>
      <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-max-w-sm">
        Start the conversation by sending a message.
      </p>
    </div>

    <!-- Messages Container -->
    <div 
      v-else 
      class="tw-h-[calc(100vh-450px)] tw-overflow-y-auto tw-p-4 tw-space-y-4" 
      ref="messagesContainer"
      @scroll="handleScroll"
    >
      <!-- Load More Button -->
      <div v-if="showLoadMore" class="tw-flex tw-justify-center tw-py-2">
        <button
          @click="loadMoreMessages"
          :disabled="loadingMore"
          class="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-transition-colors tw-text-sm"
        >
          <icon v-if="loadingMore" icon="line-md:loading-twotone-loop" class="tw-text-sm" />
          <span v-else>Load More Messages</span>
        </button>
      </div>
      <div
        v-for="message in messages"
        :key="message.id"
        :class="[
          'tw-flex tw-items-start tw-gap-3',
          isCurrentUser(message.sender_id) ? 'tw-justify-end' : ''
        ]"
      >
        <!-- Sender Avatar (left side for received messages) -->
        <div 
          v-if="!isCurrentUser(message.sender_id)"
          class="tw-w-8 tw-h-8 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden tw-cursor-pointer tw-transition-transform hover:tw-scale-110"
          @click="navigateToUserProfile(message.sender?.id)"
          :title="message.sender?.name ? `View ${message.sender.name}'s profile` : 'View profile'"
        >
          <img 
            v-if="message.sender?.profile_image" 
            :src="message.sender.profile_image" 
            :alt="message.sender.name"
            class="tw-w-full tw-h-full tw-object-cover"
          />
          <div v-else class="tw-w-full tw-h-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
            <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
          </div>
        </div>

        <!-- Message Content -->
        <div 
          :class="[
            'tw-flex tw-flex-col tw-max-w-[70%]',
            isCurrentUser(message.sender_id) ? 'tw-items-end' : 'tw-items-start'
          ]"
        >
          <!-- Sender Name (for received messages) -->
          <div v-if="!isCurrentUser(message.sender_id)" class="tw-mb-1">
            <span 
              class="tw-text-xs tw-font-medium tw-text-gray-600 tw-cursor-pointer hover:tw-text-primary-600 tw-transition-colors"
              @click="navigateToUserProfile(message.sender?.id)"
              :title="message.sender?.name ? `View ${message.sender.name}'s profile` : 'View profile'"
            >
              {{ message.sender?.name || 'Unknown User' }}
            </span>
          </div>

          <!-- Message Bubble -->
          <div 
            :class="[
              'tw-rounded-lg tw-px-3 tw-py-2 tw-max-w-full',
              isCurrentUser(message.sender_id)
                ? 'tw-bg-primary-500 tw-text-white tw-rounded-tr-none'
                : 'tw-bg-gray-100 tw-text-gray-800 tw-rounded-tl-none'
            ]"
          >
            <!-- Message Content -->
            <div class="tw-text-sm">
              <!-- Text Message -->
              <p v-if="message.message_type === 'text'" class="tw-whitespace-pre-wrap">
                {{ message.content }}
              </p>
              
              <!-- File Message -->
              <div v-else-if="message.message_type === 'file'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:file-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-primary-600'
                  ]" 
                />
                <div class="tw-flex-1 tw-min-w-0">
                  <p class="tw-font-medium tw-truncate">{{ getFileName(message.file_url) }}</p>
                  <p class="tw-text-xs tw-opacity-75">{{ getFileSize(message.file_url) }}</p>
                </div>
                <a 
                  v-if="message.file_url" 
                  :href="message.file_url" 
                  target="_blank"
                  :class="[
                    'tw-p-1 hover:tw-opacity-75 tw-transition-opacity',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-primary-600'
                  ]"
                  title="Download file"
                >
                  <icon icon="solar:download-bold-duotone" class="tw-text-sm" />
                </a>
              </div>

              <!-- Video Call Request -->
              <div v-else-if="message.message_type === 'video_call_request'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:videocamera-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-primary-600'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Video call request</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ isCurrentUser(message.sender_id) ? 'You requested a video call' : 'Wants to video call you' }}
                  </p>
                </div>
              </div>

              <!-- Voice Call Request -->
              <div v-else-if="message.message_type === 'voice_call_request'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:phone-calling-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-primary-600'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Voice call request</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ isCurrentUser(message.sender_id) ? 'You requested a voice call' : 'Wants to call you' }}
                  </p>
                </div>
              </div>

              <!-- Call Accepted -->
              <div v-else-if="message.message_type === 'call_accepted'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:check-circle-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-emerald-600'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Call accepted</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ isCurrentUser(message.sender_id) ? 'You accepted the call' : 'Accepted your call' }}
                  </p>
                </div>
              </div>

              <!-- Call Rejected -->
              <div v-else-if="message.message_type === 'call_rejected'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:close-circle-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-red-500'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Call declined</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ isCurrentUser(message.sender_id) ? 'You declined the call' : 'Declined your call' }}
                  </p>
                </div>
              </div>

              <!-- Call Ended -->
              <div v-else-if="message.message_type === 'call_ended'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:phone-calling-bold-duotone'" 
                  :class="[
                    'tw-text-lg tw-rotate-180',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-gray-600'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Call ended</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ getCallDurationText(message.metadata) }}
                  </p>
                </div>
              </div>

              <!-- Missed Call -->
              <div v-else-if="message.message_type === 'missed_call'" class="tw-flex tw-items-center tw-gap-2">
                <icon 
                  :icon="'solar:phone-calling-bold-duotone'" 
                  :class="[
                    'tw-text-lg',
                    isCurrentUser(message.sender_id) 
                      ? 'tw-text-white' 
                      : 'tw-text-amber-600'
                  ]" 
                />
                <div class="tw-flex-1">
                  <p class="tw-font-medium">Missed call</p>
                  <p class="tw-text-xs tw-opacity-75">
                    {{ isCurrentUser(message.sender_id) ? 'You missed this call' : 'Called you' }}
                  </p>
                </div>
              </div>

              <!-- Other Message Types -->
              <p v-else class="tw-text-xs tw-opacity-75">
                {{ getMessageTypeText(message.message_type) }}
              </p>
            </div>
          </div>

          <!-- Message Timestamp -->
          <div class="tw-mt-1">
            <span class="tw-text-xs tw-text-gray-500">
              {{ formatTimestamp(message.created_at) }}
            </span>
          </div>
        </div>

        <!-- Current User Avatar (right side for sent messages) -->
        <div 
          v-if="isCurrentUser(message.sender_id)"
          class="tw-w-8 tw-h-8 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden"
        >
          <img 
            v-if="currentUser?.profile_image" 
            :src="currentUser.profile_image" 
            :alt="currentUser.name"
            class="tw-w-full tw-h-full tw-object-cover"
          />
          <div v-else class="tw-w-full tw-h-full tw-bg-primary-600 tw-flex tw-items-center tw-justify-center">
            <icon icon="solar:user-bold-duotone" class="tw-text-white tw-text-sm" />
          </div>
        </div>
      </div>

      <!-- Typing Indicator -->
      <div v-if="isTyping" class="tw-flex tw-items-start tw-gap-3 tw-p-4">
        <div class="tw-w-8 tw-h-8 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
          <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
        </div>
        <div class="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-bg-gray-100 tw-rounded-lg">
          <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"></div>
          <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.1s;"></div>
          <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.2s;"></div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8">
      <div class="tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
        <icon icon="solar:warning-bold-duotone" class="tw-text-2xl tw-text-red-500" />
      </div>
      <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Failed to load messages</h3>
      <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-4">
        {{ error }}
      </p>
      <a-button @click="fetchMessages" intent="primary" size="sm">
        Try Again
      </a-button>
    </div>

    <!-- Message Input -->
    <MessageInput 
      v-if="!error && !loading"
      :conversationId="conversationId"
      :disabled="sendingMessage"
      @message-sent="handleMessageSent"
      @message-sending="handleMessageSending"
    />

    <!-- Video Call Interface -->
    <VideoCallInterface
      v-if="videoCallState.active && !videoCallState.minimized"
      :room-id="videoCallState.roomId"
      :room-url="videoCallState.roomUrl"
      :conversation-id="conversationId"
      :other-participant="otherUser"
      :call-type="videoCallState.type"
      :user-role="videoCallState.userRole"
      :call-id="videoCallState.callId"
      @call-ended="handleEndCall"
      @call-minimized="handleCallMinimized"
      @error="handleCallError"
    />
    
    <!-- Debug Info (hidden in production) -->
    <div v-if="false" class="tw-fixed tw-top-4 tw-right-4 tw-bg-black tw-text-white tw-p-4 tw-rounded-lg tw-z-[9999] tw-text-xs">
      <div>🎥 Video Call Debug:</div>
      <div>Active: {{ videoCallState.active }}</div>
      <div>Minimized: {{ videoCallState.minimized }}</div>
      <div>Type: {{ videoCallState.type }}</div>
      <div>Room ID: {{ videoCallState.roomId }}</div>
      <div>Room URL: {{ videoCallState.roomUrl }}</div>
      <div>Call ID: {{ videoCallState.callId }}</div>
      <div>User Role: {{ videoCallState.userRole }}</div>
      <div>Should Show: {{ videoCallState.active && !videoCallState.minimized }}</div>

    </div>


  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, nextTick, computed, onUnmounted, watch } from 'vue';
import { useAuthStore } from '@/stores';
import { useRouter } from 'vue-router';
import messagingApi from '@/api/messages';
import videoCallsApi from '@/api/video-calls';
import MessageInput from './MessageInput.vue';
import VideoCallButton from '@/components/video-calls/VideoCallButton.vue';
import VideoCallInterface from '@/components/video-calls/VideoCallInterface.vue';

import CallStatusIndicator from '@/components/video-calls/CallStatusIndicator.vue';
import realtimeMessagingService from '@/services/realtime-messaging';
import useAlert from '@/composables/useAlert';

const props = defineProps({
  conversationId: {
    type: String,
    required: true
  }
});

// Reactive data
const messages = ref([]);
const loading = ref(false);
const error = ref(null);
const messagesContainer = ref(null);
const sendingMessage = ref(false);
const isTyping = ref(false);
const typingUsers = ref(new Set());
const otherUser = ref(null);
const participantsLoading = ref(false);

// Video call state
const videoCallState = ref({
  active: false,
  minimized: false,
  type: 'video', // 'video' or 'voice'
  roomId: null,
  roomUrl: null,
  startTime: null,
  callId: null,
  userRole: 'participant' // Default user role
});



// Import alert function (call directly)
// const alert = useAlert(); // Not needed, call useAlert() directly

// Pagination state
const currentPage = ref(1);
const hasMoreMessages = ref(true);
const loadingMore = ref(false);
const showLoadMore = ref(false);

// Get auth store and router
const authStore = useAuthStore();
const router = useRouter();

// Computed properties
const currentUser = computed(() => authStore.user);

// Navigation methods
const navigateToUserProfile = (userId) => {
  if (userId && userId !== currentUser.value?.id) {
    router.push({ 
      name: 'shared.community-member', 
      params: { id: userId } 
    });
  }
};

// Methods
const fetchMessages = async (page = 1, append = false) => {
  if (page === 1) {
    loading.value = true;
  } else {
    loadingMore.value = true;
  }
  error.value = null;
  
  try {
    const response = await messagingApi.conversations.getMessages(props.conversationId, {
      page: page,
      per_page: 50
    });
    
    if (response.data?.success && response.data?.data) {
      const newMessages = response.data.data.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      
      if (append) {
        // Prepend older messages to the beginning
        messages.value = [...newMessages, ...messages.value];
      } else {
        // Replace messages for initial load
        messages.value = newMessages;
      }
      
      // Update pagination state
      currentPage.value = page;
      hasMoreMessages.value = response.data.data.length === 50; // If we got 50 messages, there might be more
      
      // Mark conversation as read after successfully loading messages (only on initial load)
      if (page === 1) {
        await markAsRead();
        
        // Scroll to bottom after messages are loaded
        nextTick(() => {
          scrollToBottomAfterLoad();
        });
      }
    } else {
      throw new Error('Invalid response format');
    }
  } catch (err) {
    console.error('Error fetching messages:', err);
    error.value = err.response?.data?.message || 'Failed to load messages';
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const refreshMessages = () => {
  fetchMessages();
};

const loadMoreMessages = async () => {
  if (loadingMore.value || !hasMoreMessages.value) return;
  
  const nextPage = currentPage.value + 1;
  await fetchMessages(nextPage, true);
};

const handleScroll = () => {
  if (!messagesContainer.value) return;
  
  const { scrollTop } = messagesContainer.value;
  
  // Show load more button when user scrolls near the top
  showLoadMore.value = scrollTop < 100 && hasMoreMessages.value && !loadingMore.value;
};

const markAsRead = async () => {
  try {
    await messagingApi.conversations.markAsRead(props.conversationId);
    console.log(`ChatView: Successfully marked conversation ${props.conversationId} as read`);
  } catch (err) {
    console.error('Error marking conversation as read:', err);
    // Don't throw error as this is not critical for the chat functionality
  }
};

const fetchParticipants = async () => {
  participantsLoading.value = true;
  
  try {
    const response = await messagingApi.conversations.getParticipants(props.conversationId);
    
    if (response.data?.success && response.data?.data) {
      const participants = response.data.data;
      
      // Find the other user (not the current user)
      const otherParticipant = participants.find(participant => 
        participant.id !== currentUser.value?.id
      );
      
      if (otherParticipant) {
        otherUser.value = otherParticipant;
      }
    }
  } catch (err) {
    console.error('Error fetching participants:', err);
  } finally {
    participantsLoading.value = false;
  }
};

const isCurrentUser = (senderId) => {
  return currentUser.value?.id === senderId;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${displayMinutes} ${ampm}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getUTCDate();
        return `${month} ${day}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

const getFileName = (fileUrl) => {
  if (!fileUrl) return 'Unknown file';
  return fileUrl.split('/').pop() || 'Unknown file';
};

const getFileSize = () => {
  // This would need to be implemented based on your file handling
  return 'Unknown size';
};

const getMessageTypeText = (messageType) => {
  const typeMap = {
    'text': 'Text message',
    'file': 'File message',
    'image': 'Image message',
    'video': 'Video message',
    'audio': 'Audio message'
  };
  return typeMap[messageType] || 'Message';
};

const getCallDurationText = (metadata) => {
  if (!metadata?.duration) {
    return 'Call ended';
  }
  
  const duration = metadata.duration;
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  
  if (minutes > 0) {
    return `Duration: ${minutes}m ${seconds}s`;
  } else {
    return `Duration: ${seconds}s`;
  }
};

const scrollToBottom = () => {
  if (messagesContainer.value) {
    // Use requestAnimationFrame for smooth scrolling
    requestAnimationFrame(() => {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    });
  }
};

const scrollToBottomAfterLoad = () => {
  // Add a small delay to ensure DOM is fully updated
  setTimeout(() => {
    scrollToBottom();
  }, 100);
};

// Auto-scroll when messages change
watch(messages, () => {
  nextTick(() => {
    scrollToBottom();
  });
}, { deep: true });

// Also watch for when loading state changes to false (messages loaded)
watch(loading, (newLoading) => {
  if (!newLoading && messages.value.length > 0) {
    // Messages have finished loading, scroll to bottom
    nextTick(() => {
      scrollToBottomAfterLoad();
    });
  }
});

// Message handling methods
const handleMessageSending = () => {
  sendingMessage.value = true;
  // You can add optimistic UI updates here if needed
};

const handleMessageSent = async (messageData) => {
  try {
    // Send the message via API
    const response = await messagingApi.messages.sendText(props.conversationId, {
      content: messageData.content
    });
    
    if (response.data?.success) {
      // Add the sent message to the local messages array
      const sentMessage = {
        id: response.data.data?.id || Date.now(), // Use response ID or fallback
        content: messageData.content,
        message_type: 'text',
        sender_id: currentUser.value?.id,
        sender: {
          id: currentUser.value?.id,
          name: currentUser.value?.name,
          profile_image: currentUser.value?.profile_image
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Add to messages array - auto-scroll will be handled by the watcher
      messages.value.push(sentMessage);
    } else {
      throw new Error('Failed to send message');
    }
  } catch (err) {
    console.error('Error sending message:', err);
    // You can add error handling here (show alert, etc.)
  } finally {
    sendingMessage.value = false;
  }
};

// Video call handlers
const handleCallInitiated = (callData) => {
  console.log('📞 CALL INITIATED HANDLED:', {
    timestamp: new Date().toISOString(),
    callData: callData,
    conversationId: props.conversationId
  });
  
  // Start the video call interface
  videoCallState.value = {
    active: true,
    minimized: false,
    type: callData.callType,
    roomId: callData.roomId,
    roomUrl: callData.roomUrl,
    startTime: new Date(),
    callId: callData.callId,
    userRole: callData.userRole || 'host' // Include user role (caller is usually host)
  };
};

const handleCallError = (error) => {
  console.error('❌ CALL ERROR HANDLED:', {
    timestamp: new Date().toISOString(),
    error: error,
    conversationId: props.conversationId
  });
  
  try {
    useAlert(error, 'danger');
  } catch (alertError) {
    console.error('Alert error:', alertError);
    console.log('Call error:', error);
  }
};







const handleRestoreCall = (callData) => {
  console.log('🔄 CALL RESTORED:', {
    timestamp: new Date().toISOString(),
    callData: callData,
    conversationId: props.conversationId
  });
  videoCallState.value.minimized = false;
};

const handleEndCall = async (callData) => {
  console.log('🔚 ENDING CALL:', {
    timestamp: new Date().toISOString(),
    callData: callData,
    conversationId: props.conversationId
  });
  
  try {
    // End the call via API using callId if available, otherwise fallback to roomId
    const callIdToUse = callData.callId || callData.roomId;
    
    console.log('🔚 ENDING CALL VIA API:', {
      timestamp: new Date().toISOString(),
      callIdToUse: callIdToUse,
      callIdFromData: callData.callId,
      roomIdFromData: callData.roomId,
      fallbackUsed: !callData.callId,
      reason: 'Using callId is preferred, roomId is fallback'
    });
    
    await videoCallsApi.calls.end(callIdToUse, {
      duration: callData.duration,
      reason: 'ended_by_user'
    });
    
    console.log('✅ CALL ENDED SUCCESSFULLY VIA API:', {
      timestamp: new Date().toISOString(),
      callId: callIdToUse,
      duration: callData.duration,
      idType: callData.callId ? 'callId' : 'roomId (fallback)'
    });
    
    // Send call ended message
    await messagingApi.messages.sendCallEnded(callData.conversationId, {
      metadata: {
        call_type: callData.callType,
        duration: callData.duration,
        reason: 'ended_by_user',
        call_id: callData.callId
      }
    });
    
    console.log('💬 CALL ENDED MESSAGE SENT:', {
      timestamp: new Date().toISOString(),
      conversationId: callData.conversationId,
      metadata: {
        call_type: callData.callType,
        duration: callData.duration,
        reason: 'ended_by_user',
        call_id: callData.callId
      }
    });
    
  } catch (error) {
    console.error('❌ ERROR ENDING CALL:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      response: error.response?.data,
      callData: callData
    });
  }
  
  // Reset call state
  videoCallState.value = {
    active: false,
    minimized: false,
    type: 'video',
    roomId: null,
    roomUrl: null,
    startTime: null,
    callId: null,
    userRole: 'participant'
  };
};

const handleCallMinimized = () => {
  console.log('📱 CALL MINIMIZED:', {
    timestamp: new Date().toISOString(),
    conversationId: props.conversationId
  });
  videoCallState.value.minimized = true;
};

// Real-time messaging setup
const setupRealtimeMessaging = async () => {
  // Join the conversation channel
  await realtimeMessagingService.joinConversation(props.conversationId, {
    showNotifications: false // Disable notifications since we're in the chat view
  });

  // Listen for new messages
  realtimeMessagingService.on('text-message', (event) => {
    // Add new message to the list
    messages.value.push(event);
    // Auto-scroll will be handled by the watcher
  });

  // Listen for typing indicators
  realtimeMessagingService.on('typing-started', (event) => {
    const { sender } = event;
    if (sender && !isCurrentUser(sender.id)) {
      typingUsers.value.add(sender.id);
      isTyping.value = true;
    }
  });

  realtimeMessagingService.on('typing-stopped', (event) => {
    const { sender } = event;
    if (sender && !isCurrentUser(sender.id)) {
      typingUsers.value.delete(sender.id);
      isTyping.value = typingUsers.value.size > 0;
    }
  });

  // Listen for other message types
  realtimeMessagingService.on('file-message', (event) => {
    messages.value.push(event);
    // Auto-scroll will be handled by the watcher
  });

  realtimeMessagingService.on('system-message', (event) => {
    messages.value.push(event);
    // Auto-scroll will be handled by the watcher
  });

  // Listen for video call events (only for message display, not handling)
  realtimeMessagingService.on('video-call-request', (event) => {
    messages.value.push(event);
  });

  realtimeMessagingService.on('voice-call-request', (event) => {
    messages.value.push(event);
  });

  realtimeMessagingService.on('call-accepted', (event) => {
    messages.value.push(event);
  });

  realtimeMessagingService.on('call-rejected', (event) => {
    messages.value.push(event);
  });

  realtimeMessagingService.on('call-ended', (event) => {
    messages.value.push(event);
  });

  realtimeMessagingService.on('missed-call', (event) => {
    messages.value.push(event);
  });
};

// Cleanup real-time messaging
const cleanupRealtimeMessaging = () => {
  realtimeMessagingService.leaveConversation(props.conversationId);
  realtimeMessagingService.off('text-message');
  realtimeMessagingService.off('typing-started');
  realtimeMessagingService.off('typing-stopped');
  realtimeMessagingService.off('file-message');
  realtimeMessagingService.off('system-message');
  realtimeMessagingService.off('video-call-request');
  realtimeMessagingService.off('voice-call-request');
  realtimeMessagingService.off('call-accepted');
  realtimeMessagingService.off('call-rejected');
  realtimeMessagingService.off('call-ended');
  realtimeMessagingService.off('missed-call');
};

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchMessages(),
    fetchParticipants()
  ]);
  await setupRealtimeMessaging();
  
  // Ensure scroll to bottom after everything is loaded
  nextTick(() => {
    scrollToBottomAfterLoad();
    // Initialize scroll detection
    handleScroll();
  });
});

onUnmounted(() => {
  cleanupRealtimeMessaging();
});
</script>

<style scoped>
/* Custom scrollbar for messages container */
.tw-overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.tw-overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Ensure proper height constraints */
.tw-h-full {
  height: 100%;
  min-height: 0;
}

/* Smooth scrolling for messages container */
.tw-overflow-y-auto {
  scroll-behavior: smooth;
}
</style>